import { Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { catchError, finalize, forkJoin, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { FuseAlertComponent } from '../../../../../../../@fuse/components/alert';
import { FuseLoadingService } from '../../../../../../../@fuse/services/loading';
import { Directory } from '../../../../../../shared/models/directory';
import { Prompt } from '../../../../../../shared/models/prompt';
import { PromptImportExportService } from '../../../../../../shared/services/prompt-import-export.service';
import { PromptService } from '../../../../../../shared/services/prompt.service';

interface ExportSummary {
    fileName: string;
    directoryNames: string;
    exportedCount: number;
}

type DirectoryWithPrompts = Directory & { prompts?: Prompt[] };

@Component({
    selector: 'app-export-prompts',
    templateUrl: './export-prompts.component.html',
    imports: [MatExpansionModule, MatCheckboxModule, FuseAlertComponent],
})
export class ExportPromptsComponent implements OnInit, OnDestroy {
    private readonly promptService = inject(PromptService);
    private readonly promptImportExportService = inject(PromptImportExportService);
    private readonly fuseLoadingService = inject(FuseLoadingService);
    private readonly destroy$ = new Subject<void>();

    protected readonly allDirectories = signal<DirectoryWithPrompts[]>([]);
    protected readonly selectedPromptUids = signal<Set<string>>(new Set());
    protected readonly showAlert = signal(false);
    protected readonly alertType = signal<'success' | 'error'>('success');
    protected readonly alertMessage = signal('');
    protected readonly exportSummary = signal<ExportSummary | null>(null);

    public readonly exportCompleted = signal(false);
    public readonly selectedCount = computed(() => {
        const availableUids = new Set(
            this.allDirectories()
                .flatMap((d) => d.prompts ?? [])
                .map((p) => p.uid),
        );
        return [...this.selectedPromptUids()].filter((uid) => availableUids.has(uid)).length;
    });
    public readonly directories = input.required<Observable<Directory[]>>();

    public ngOnInit() {
        this.directories()
            .pipe(
                switchMap((directories) => {
                    if (!directories.length) {
                        return of([]);
                    }
                    const directoryWithPrompts$ = directories.map((directory) =>
                        this.promptService.getAllInDirectory(directory.uid).pipe(
                            take(1),
                            catchError(() => {
                                this.showAlert.set(true);
                                this.alertType.set('error');
                                this.alertMessage.set('Ein oder mehrere Verzeichnisse konnten nicht geladen werden.');
                                return of([]);
                            }),
                            map((prompts) => ({ ...directory, prompts })),
                        ),
                    );
                    return forkJoin(directoryWithPrompts$);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (directories) => {
                    this.allDirectories.set(directories);
                },
                error: () => {
                    this.showAlert.set(true);
                    this.alertType.set('error');
                    this.alertMessage.set('Die Prompt-Verzeichnisse konnten nicht geladen werden.');
                },
            });
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected isSelected(promptUid: string): boolean {
        return this.selectedPromptUids().has(promptUid);
    }

    protected togglePromptSelection(promptUid: string, selected: boolean) {
        this.selectedPromptUids.update((previousSelectedUids) => {
            const newSelectedUids = new Set(previousSelectedUids);
            if (selected) {
                newSelectedUids.add(promptUid);
            } else {
                newSelectedUids.delete(promptUid);
            }
            return newSelectedUids;
        });
    }

    protected areAllPromptsSelected(directory: DirectoryWithPrompts): boolean {
        return !!directory.prompts?.length && directory.prompts.every((prompt) => this.isSelected(prompt.uid));
    }

    protected areSomePromptsSelected(directory: DirectoryWithPrompts): boolean {
        return !!directory.prompts?.some((prompt) => this.isSelected(prompt.uid));
    }

    protected toggleDirectorySelection(directoryUid: string, selected: boolean) {
        const directory = this.allDirectories().find((directory) => directory.uid === directoryUid);
        if (!directory?.prompts) {
            return;
        }

        this.selectedPromptUids.update((previousSelectedUids) => {
            const newSelectedUids = new Set(previousSelectedUids);
            for (const prompt of directory.prompts!) {
                if (selected) {
                    newSelectedUids.add(prompt.uid);
                } else {
                    newSelectedUids.delete(prompt.uid);
                }
            }
            return newSelectedUids;
        });
    }

    public exportPrompts() {
        this.fuseLoadingService.show();

        const allDirectories = this.allDirectories();
        const selectedUids = this.selectedPromptUids();

        const selectedPrompts = allDirectories
            .flatMap((directory) => directory.prompts ?? [])
            .filter((prompt) => selectedUids.has(prompt.uid));

        const selectedDirectoryNames = allDirectories
            .filter((directory) => directory.prompts?.some((prompt) => selectedUids.has(prompt.uid)))
            .map((directory) => directory.name);

        const selectedDirectoryNamesDisplay = selectedDirectoryNames
            .slice(0, 8)
            .concat(selectedDirectoryNames.length > 8 ? '…' : [])
            .join(', ');

        this.promptImportExportService
            .exportPrompts(selectedPrompts)
            .pipe(finalize(() => this.fuseLoadingService.hide()))
            .subscribe({
                next: (filename) => {
                    this.alertType.set('success');
                    this.alertMessage.set(
                        `Der Download für die Datei ${filename} mit ${selectedPrompts.length} Prompts wurde erfolgreich gestartet.`,
                    );
                    this.showAlert.set(true);
                    this.exportSummary.set({
                        fileName: filename,
                        directoryNames: selectedDirectoryNamesDisplay,
                        exportedCount: selectedPrompts.length,
                    });
                    this.exportCompleted.set(true);
                },
                error: (error) => {
                    this.alertType.set('error');
                    this.alertMessage.set(error.message || 'Beim Exportieren der Prompts ist ein Fehler aufgetreten.');
                    this.showAlert.set(true);
                    this.exportCompleted.set(true);
                },
            });
    }
}
